/**
 * Shared in-memory shape primitives for the tactical board AND video
 * telestrator.
 *
 * Both surfaces let coaches draw the same set of vector primitives —
 * arrows, lines, freehand strokes, labels, and zones — with identical
 * interaction (drag-to-draw, drag-to-move, color/stroke palette,
 * Konva.Transformer resize for zones). The canonical in-memory shape
 * lives here so the canvas components, hooks, and editor store all
 * speak one language regardless of which surface is active.
 *
 * The legacy on-disk schemas DIVERGE on naming:
 *   - tactical_board_json: `kind`, `stroke_width`
 *   - coaching_notes.drawing / coaching_clips.drawing: `type`, `width`
 *
 * Surface-specific schema modules (`board-schema.ts` /
 * `telestrator-schema.ts`) own the I/O adapters that translate
 * between this canonical model and the on-disk shape. In-memory code
 * (components, hooks, store) never sees the legacy `type` / `width`
 * field names.
 *
 * Surface-specific shape kinds (`player` / `ball` tokens for the
 * tactical board; `spotlight` / `dim` / `formation` overlays for the
 * telestrator) live in the surface modules — they don't belong in
 * the shared union.
 */

import { clamp01, type Point } from "./coords";

// ---------------------------------------------------------------------------
// Palette + constants — SHARED across surfaces
// ---------------------------------------------------------------------------

/** Color palette used by BOTH surfaces. The legacy tactical-board
 * code uses these 6 colors via `_VALID_BOARD_COLORS` (models.py); the
 * legacy telestrator (`js/coaching/review.js:867`) uses the same set
 * with one additional alias (`#fde047` legacy default) kept for
 * backwards compatibility with shapes saved before Phase 6d-2. */
export const SHARED_COLORS = [
  "#38bdf8", // Sky blue
  "#f97316", // Orange
  "#22c55e", // Green
  "#facc15", // Yellow
  "#f43f5e", // Red
  "#ffffff", // White
] as const;

export const LEGACY_DEFAULT_COLOR = "#fde047";

/** All accepted colors (palette + legacy default). The editor only
 * exposes the 6-color palette; the legacy default is accepted for
 * older saved shapes that may still carry it. */
export const ALL_VALID_COLORS = [...SHARED_COLORS, LEGACY_DEFAULT_COLOR] as const;

export type SharedColor = (typeof ALL_VALID_COLORS)[number];

/** Stroke width bounds (matches both backends: `_normalize_board_stroke_width`
 * for tactical board, and the telestrator's `min="2" max="10"` slider). */
export const SHARED_STROKE_WIDTH_MIN = 2;
export const SHARED_STROKE_WIDTH_MAX = 10;
export const SHARED_STROKE_WIDTH_DEFAULT = 3;

/** Caps that apply to both surfaces. Surface-specific schemas may
 * lower these (tactical board's 40-shape cap is enforced in
 * board-schema; the telestrator currently has no published cap). */
export const SHARED_MAX_FREEHAND_POINTS = 200;
export const SHARED_MIN_FREEHAND_POINTS = 2;
export const SHARED_MAX_LABEL_LENGTH = 80;
export const SHARED_MIN_ZONE_SIZE = 0.02;

// ---------------------------------------------------------------------------
// SharedShape — the canonical in-memory shape used by both surfaces
// ---------------------------------------------------------------------------

/** Common fields every shape carries. */
type SharedShapeBase = {
  /** Stable client id (generated on creation). Persisted only on the
   * tactical board (matches legacy behavior); the telestrator strips
   * IDs on save to keep the drawing blob small. */
  id?: string;
  /** Palette color. `undefined` falls through to the legacy default
   * at render time. */
  color?: SharedColor;
  /** Bounded [2, 10] integer. `undefined` falls through to
   * `SHARED_STROKE_WIDTH_DEFAULT`. */
  strokeWidth?: number;
};

/** Arrow — two endpoints, head at (x2, y2). */
export type SharedArrowShape = SharedShapeBase & {
  kind: "arrow";
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

/** Line — two endpoints, no arrowhead. Identical to arrow in storage
 * but rendered without the head. */
export type SharedLineShape = SharedShapeBase & {
  kind: "line";
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

/** Zone — rectangular region. Backend rejects w/h<MIN, and rejects
 * x+w > 1.0001 / y+h > 1.0001. */
export type SharedZoneShape = SharedShapeBase & {
  kind: "zone";
  x: number;
  y: number;
  w: number;
  h: number;
  /** Optional caption rendered inside the zone. */
  text?: string;
};

/** Label — single anchor point + required text. */
export type SharedLabelShape = SharedShapeBase & {
  kind: "label";
  x: number;
  y: number;
  text: string;
};

/** Freehand — list of 2..200 points. */
export type SharedFreehandShape = SharedShapeBase & {
  kind: "freehand";
  points: Point[];
};

/** Discriminated union of every shape both surfaces share. */
export type SharedShape =
  | SharedArrowShape
  | SharedLineShape
  | SharedZoneShape
  | SharedLabelShape
  | SharedFreehandShape;

/** The 5 shape kinds in the `SharedShape` union. Used to drive shape-
 * specific picker UI and as the closed-set check at the I/O boundary
 * (both surfaces extend this with their own surface-only kinds — the
 * tactical board adds `player`/`ball` tokens; the telestrator adds
 * `spotlight`/`dim`/`formation`). */
export const SHARED_SHAPE_KINDS = [
  "arrow",
  "line",
  "zone",
  "label",
  "freehand",
] as const;
export type SharedShapeKind = (typeof SHARED_SHAPE_KINDS)[number];

/** Tool kinds the editor exposes for SHARED shapes. Includes `select`
 * (a tool, not a shape) — surface-specific tools (player/ball/
 * spotlight/dim/formation) extend this set per-surface. */
export const SHARED_TOOL_KINDS = [
  "select",
  ...SHARED_SHAPE_KINDS,
] as const;
export type SharedToolKind = (typeof SHARED_TOOL_KINDS)[number];

// ---------------------------------------------------------------------------
// Predicates
// ---------------------------------------------------------------------------

export function isSharedColor(value: unknown): value is SharedColor {
  return (
    typeof value === "string" && (ALL_VALID_COLORS as readonly string[]).includes(value)
  );
}

export function isSharedStrokeWidth(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= SHARED_STROKE_WIDTH_MIN &&
    value <= SHARED_STROKE_WIDTH_MAX
  );
}

export function isValidUnit(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1;
}

export function isSharedShapeKind(value: unknown): value is SharedShapeKind {
  return (
    typeof value === "string" &&
    (SHARED_SHAPE_KINDS as readonly string[]).includes(value)
  );
}

// ---------------------------------------------------------------------------
// Normalizers — coerce/strip invalid inputs without throwing. The editor
// uses these to clean a shape after a drag before committing.
// ---------------------------------------------------------------------------

/** Drop a color value that isn't on the palette (silent — matches the
 * backend behavior of dropping unknown colors rather than 422'ing). */
export function normalizeSharedColor(value: unknown): SharedColor | undefined {
  return isSharedColor(value) ? value : undefined;
}

/** Round + clamp stroke width to the [2, 10] integer range. Returns
 * `undefined` for non-finite / out-of-range input so the renderer
 * uses the default. */
export function normalizeSharedStrokeWidth(value: unknown): number | undefined {
  if (!isSharedStrokeWidth(value)) return undefined;
  return Math.round(value);
}

// ---------------------------------------------------------------------------
// Validators — return { ok: true, value } | { ok: false, reason }.
// Surface-specific schemas wrap these to add per-surface caps.
// ---------------------------------------------------------------------------

export type ValidationResult<T> =
  | { ok: true; value: T }
  | { ok: false; reason: string };

function readId(input: Record<string, unknown>): { id?: string } {
  return typeof input.id === "string" && input.id ? { id: input.id } : {};
}

function readMeta(input: Record<string, unknown>): {
  color?: SharedColor;
  strokeWidth?: number;
} {
  const out: { color?: SharedColor; strokeWidth?: number } = {};
  const color = normalizeSharedColor(input.color);
  if (color !== undefined) out.color = color;
  const strokeWidth = normalizeSharedStrokeWidth(input.strokeWidth);
  if (strokeWidth !== undefined) out.strokeWidth = strokeWidth;
  return out;
}

export function validateSharedShape(
  input: unknown,
  index = 0,
): ValidationResult<SharedShape> {
  if (typeof input !== "object" || input === null) {
    return { ok: false, reason: `shapes[${index}] must be an object` };
  }
  const s = input as Record<string, unknown>;
  if (!isSharedShapeKind(s.kind)) {
    return {
      ok: false,
      reason: `shapes[${index}].kind must be one of arrow/line/zone/label/freehand`,
    };
  }

  if (s.kind === "arrow" || s.kind === "line") {
    for (const k of ["x1", "y1", "x2", "y2"] as const) {
      if (!isValidUnit(s[k])) {
        return {
          ok: false,
          reason: `shapes[${index}].${k} must be a number in [0, 1]`,
        };
      }
    }
    return {
      ok: true,
      value: {
        kind: s.kind as "arrow" | "line",
        x1: s.x1 as number,
        y1: s.y1 as number,
        x2: s.x2 as number,
        y2: s.y2 as number,
        ...readMeta(s),
        ...readId(s),
      },
    };
  }

  if (s.kind === "zone") {
    if (!isValidUnit(s.x) || !isValidUnit(s.y)) {
      return { ok: false, reason: `shapes[${index}] zone x/y must be in [0, 1]` };
    }
    if (!isValidUnit(s.w) || !isValidUnit(s.h)) {
      return { ok: false, reason: `shapes[${index}] zone w/h must be in [0, 1]` };
    }
    const w = s.w as number;
    const h = s.h as number;
    if (w < SHARED_MIN_ZONE_SIZE || h < SHARED_MIN_ZONE_SIZE) {
      return {
        ok: false,
        reason: `shapes[${index}] zone size must be >= ${SHARED_MIN_ZONE_SIZE}`,
      };
    }
    if ((s.x as number) + w > 1.0001 || (s.y as number) + h > 1.0001) {
      return {
        ok: false,
        reason: `shapes[${index}] zone extends past surface bounds`,
      };
    }
    const text =
      typeof s.text === "string" && s.text.length <= SHARED_MAX_LABEL_LENGTH
        ? s.text
        : undefined;
    return {
      ok: true,
      value: {
        kind: "zone",
        x: s.x as number,
        y: s.y as number,
        w,
        h,
        ...(text !== undefined ? { text } : {}),
        ...readMeta(s),
        ...readId(s),
      },
    };
  }

  if (s.kind === "label") {
    if (!isValidUnit(s.x) || !isValidUnit(s.y)) {
      return { ok: false, reason: `shapes[${index}] label x/y must be in [0, 1]` };
    }
    if (typeof s.text !== "string" || !s.text) {
      return { ok: false, reason: `shapes[${index}] label requires text` };
    }
    if (s.text.length > SHARED_MAX_LABEL_LENGTH) {
      return {
        ok: false,
        reason: `shapes[${index}] label text exceeds max length ${SHARED_MAX_LABEL_LENGTH}`,
      };
    }
    return {
      ok: true,
      value: {
        kind: "label",
        x: s.x as number,
        y: s.y as number,
        text: s.text,
        ...readMeta(s),
        ...readId(s),
      },
    };
  }

  if (s.kind === "freehand") {
    if (!Array.isArray(s.points)) {
      return {
        ok: false,
        reason: `shapes[${index}] freehand points must be a list`,
      };
    }
    if (s.points.length < SHARED_MIN_FREEHAND_POINTS) {
      return {
        ok: false,
        reason: `shapes[${index}] freehand requires at least ${SHARED_MIN_FREEHAND_POINTS} points`,
      };
    }
    if (s.points.length > SHARED_MAX_FREEHAND_POINTS) {
      return {
        ok: false,
        reason: `shapes[${index}] freehand exceeds max ${SHARED_MAX_FREEHAND_POINTS} points`,
      };
    }
    const cleanPoints: Point[] = [];
    for (let i = 0; i < s.points.length; i++) {
      const p = s.points[i];
      if (typeof p !== "object" || p === null) {
        return {
          ok: false,
          reason: `shapes[${index}].points[${i}] must be an object`,
        };
      }
      const pt = p as Record<string, unknown>;
      if (!isValidUnit(pt.x) || !isValidUnit(pt.y)) {
        return {
          ok: false,
          reason: `shapes[${index}].points[${i}] must have x and y in [0, 1]`,
        };
      }
      cleanPoints.push({ x: pt.x as number, y: pt.y as number });
    }
    return {
      ok: true,
      value: {
        kind: "freehand",
        points: cleanPoints,
        ...readMeta(s),
        ...readId(s),
      },
    };
  }

  return {
    ok: false,
    reason: `shapes[${index}].kind not handled: ${String(s.kind)}`,
  };
}

// ---------------------------------------------------------------------------
// Clamp helpers — for in-drag use. Don't reject; coerce to in-bounds.
// ---------------------------------------------------------------------------

/** Clamp coordinates of any shared shape into [0, 1]. Zones additionally
 * collapse to the minimum size and trim to fit pitch bounds. */
export function clampSharedShape(shape: SharedShape): SharedShape {
  switch (shape.kind) {
    case "arrow":
    case "line":
      return {
        ...shape,
        x1: clamp01(shape.x1),
        y1: clamp01(shape.y1),
        x2: clamp01(shape.x2),
        y2: clamp01(shape.y2),
      };
    case "zone": {
      const x = clamp01(shape.x);
      const y = clamp01(shape.y);
      const w = Math.min(Math.max(shape.w, SHARED_MIN_ZONE_SIZE), 1 - x);
      const h = Math.min(Math.max(shape.h, SHARED_MIN_ZONE_SIZE), 1 - y);
      return { ...shape, x, y, w, h };
    }
    case "label":
      return { ...shape, x: clamp01(shape.x), y: clamp01(shape.y) };
    case "freehand":
      return {
        ...shape,
        points: shape.points.map((p) => ({ x: clamp01(p.x), y: clamp01(p.y) })),
      };
  }
}
